import { useState, useEffect, useRef, useCallback } from "react";

const useInfiniteScroll = (fetchFunction, initialPage = 1, limit = 10) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);

  // Ref for intersection observer
  const loadMoreRef = useRef(null);

  // Reset function to clear all data and start fresh
  const reset = useCallback(() => {
    setData([]);
    setCurrentPage(initialPage);
    setHasMore(true);
    setTotalPages(0);
    setError(null);
  }, [initialPage]);

  // Load data function
  const loadData = useCallback(
    async (page = initialPage, isLoadMore = false) => {
      if (loading) return;

      try {
        setLoading(true);
        setError(null);

        const result = await fetchFunction({
          page,
          limit,
          isLoadMore,
        });

        if (result?.success && result?.response) {
          const { files, paginationData } = result.response;

          if (isLoadMore) {
            // Append new data to existing
            setData((prevData) => [...prevData, ...(files || [])]);
          } else {
            // Replace data for initial load
            setData(files || []);
          }

          // Update pagination state
          setCurrentPage(paginationData.page);
          setTotalPages(paginationData.totalPages);
          setHasMore(paginationData.hasNextPage);
        }
      } catch (err) {
        setError(err);
        console.error("useInfiniteScroll error:", err);
      } finally {
        setLoading(false);
      }
    },
    [fetchFunction, limit, loading, initialPage]
  );

  // Load more data
  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      const nextPage = currentPage + 1;
      loadData(nextPage, true);
    }
  }, [hasMore, loading, currentPage, loadData]);

  // Intersection Observer setup
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loadMore, hasMore, loading]);

  // Initial load
  useEffect(() => {
    loadData(initialPage, false);
  }, []);

  // Refresh function - reload from first page
  const refresh = useCallback(() => {
    reset();
    setTimeout(() => {
      loadData(initialPage, false);
    }, 100);
  }, [reset, loadData, initialPage]);

  return {
    data,
    loading,
    hasMore,
    currentPage,
    totalPages,
    error,
    loadMoreRef,
    refresh,
    reset,
    loadMore: loadMore,
    isLastPage: currentPage >= totalPages,
  };
};

export default useInfiniteScroll;
